import React, { useEffect, useState, useMemo } from "react";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  TextField,
  CircularProgress,
  Paper,
  Autocomplete,
} from "@mui/material";
import {
  fetchAnalyticsReport,
  fetchAnalyticsReportList,
} from "../../../services/Analytics/analyticsService";
import "./AnalyticsPage.css";
import { useSelector } from "react-redux";

const AnalyticsPage = () => {
  const [reportId, setReportId] = useState(null);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [reportName, setReportName] = useState(""); 
  const [reportList, setReportList] = useState([]);
  const [loading, setLoading] = useState(true);

  const {
    approvedRegions = [""],
    regionPlantMapping = {},
    ctAppRoles = [], 
  } = useSelector((state) => state.user || {});

  
  // ---------------- FETCH & FILTER REPORT LIST ----------------
  useEffect(() => {
    const fetchList = async () => {
      try {
        const { list } = await fetchAnalyticsReportList();
        const rawList = list || [];

        // Check if user has the specific inventory role
        const hasInventoryAccess = ctAppRoles?.includes("CT-INVENTORY");

        let finalViewableList;

        if (hasInventoryAccess) {
          // 1. Show ALL reports if they have the role
          finalViewableList = rawList;
        } else {
          // 2. Filter OUT inventory/ageing reports if they DON'T have the role
          finalViewableList = rawList.filter((name) => {
            const lowerName = name.toLowerCase();
            return !lowerName.includes("inventory");
          });
        }

        setReportList(finalViewableList);

        // Auto-select the first available report if current selection is invalid
        if (finalViewableList.length > 0 && !finalViewableList.includes(reportName)) {
          setReportName(finalViewableList[0]);
        }
      } catch (error) {
        console.error("Error fetching report list:", error);
      }
    };
    fetchList();
  }, [ctAppRoles]); 

  // ---------------- POWER BI REGION/PLANT CALCULATIONS ----------------
  const availablePlants = useMemo(() => {
    const plantSet = new Set();
    approvedRegions.forEach((region) => {
      const plants = regionPlantMapping[region] || [];
      plants.forEach((plant) => plantSet.add(plant));
    });
    return Array.from(plantSet).map((p) => ({ id: p, name: p }));
  }, [approvedRegions, regionPlantMapping]);

  const powerBiFilters = useMemo(() => {
    const filters = [];
    if (approvedRegions.length > 0) {
      filters.push({
        $schema: "http://powerbi.com/product/schema#basic",
        target: { table: "USER_REGION_PLANT_MAPPING", column: "Region" },
        operator: "In",
        values: approvedRegions,
      });
    }
    if (availablePlants.length > 0) {
      filters.push({
        $schema: "http://powerbi.com/product/schema#basic",
        target: { table: "USER_REGION_PLANT_MAPPING", column: "ALL_PLANTS" },
        operator: "In",
        values: availablePlants.map((p) => p.id),
      });
    }
    return filters;
  }, [approvedRegions, availablePlants]);

  // ---------------- FETCH SPECIFIC REPORT DATA ----------------
  useEffect(() => {
    if (!reportName) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const { reportId, embedUrl, acessToken } = await fetchAnalyticsReport(reportName);
        setReportId(reportId);
        setEmbedUrl(embedUrl);
        setAccessToken(acessToken);
      } catch (error) {
        console.error("Error fetching analytics data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [reportName]);

  // ---------------- EMBED CONFIG ----------------
  const embedConfig = {
    type: "report",
    id: reportId,
    embedUrl,
    accessToken,
    tokenType: models.TokenType.Embed,
    filters: powerBiFilters,
    settings: {
      panes: { filters: { expanded: false, visible: false } },
      navContentPaneEnabled: false,
      layoutType: models.LayoutType.Custom,
      customLayout: { displayOption: models.DisplayOption.FitToWidth },
    },
  };

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <AppBar position="static" elevation={1} color="default">
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Analytics Reports</Typography>
          <Autocomplete
            options={reportList}
            value={reportName || null}
            onChange={(event, newValue) => setReportName(newValue)}
            sx={{
              minWidth: 300,
              background: "white",
              borderRadius: 1,
              "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            }}
            renderInput={(params) => <TextField {...params} label="Select Report" size="small" />}
          />
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, p: 2 }}>
        <Paper sx={{ height: "100%", borderRadius: 2, overflow: "hidden", position: "relative" }}>
          {loading ? (
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : reportId && embedUrl && accessToken ? (
            <PowerBIEmbed
              embedConfig={embedConfig}
              cssClassName="report-style-class"
            />
          ) : (
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography variant="body1" color="textSecondary">No report available</Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default AnalyticsPage;